import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'

// ─── Array color scheme ────────────────────────────────────────────────────────
const ARR_ACCENT      = '#30c0a0'
const ARR_HDR_BG      = '#041a16'
const ARR_BG_IDLE     = '#020e0c'
const ARR_BG_SEL      = '#04140f'
const ARR_BORDER_SEL  = '#30c0a0'
const ARR_BORDER_IDLE = '#0a2820'

// ─── Map color scheme ──────────────────────────────────────────────────────────
const MAP_ACCENT      = '#d09020'
const MAP_HDR_BG      = '#1a1004'
const MAP_BG_IDLE     = '#100a02'
const MAP_BG_SEL      = '#14100a'
const MAP_BORDER_SEL  = '#d09020'
const MAP_BORDER_IDLE = '#302010'

// ─── Shared pin colors ─────────────────────────────────────────────────────────
const IDX_COLOR  = '#60b0d0'  // int / index
const VAL_COLOR  = '#c060e8'  // generic value
const BOOL_COLOR = '#d06060'  // bool output

// ─── Exec handle style ─────────────────────────────────────────────────────────
const EXEC = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }

// ─── Shared variable selector ──────────────────────────────────────────────────
function VarSelect({ value, onChange, variables, filterType, accent }) {
  const stop = e => e.stopPropagation()
  const filtered = (variables ?? []).filter(v => v.type === filterType)
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange?.(e.target.value)}
      onMouseDown={stop}
      style={{
        flex: 1, minWidth: 0, background: '#040608', border: `1px solid ${accent}44`,
        color: accent, padding: '2px 5px', borderRadius: 3,
        fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none', cursor: 'pointer',
      }}
    >
      <option value="">— pick var —</option>
      {filtered.map(v => (
        <option key={v.id} value={v.name}>{v.name}</option>
      ))}
    </select>
  )
}

// ─── Shared index / key input ──────────────────────────────────────────────────
function NumberInput({ value, onChange, placeholder = '0' }) {
  return (
    <input
      type="number" step={1}
      value={value ?? 0}
      onChange={e => onChange?.(parseInt(e.target.value) || 0)}
      onMouseDown={e => e.stopPropagation()}
      placeholder={placeholder}
      style={{
        width: 48, background: '#040608', border: `1px solid ${IDX_COLOR}33`,
        color: IDX_COLOR, padding: '2px 5px', borderRadius: 3,
        fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
      }}
    />
  )
}

function TextInput({ value, onChange, placeholder = '', accent }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={e => onChange?.(e.target.value)}
      onMouseDown={e => e.stopPropagation()}
      placeholder={placeholder}
      style={{
        flex: 1, background: '#040608', border: `1px solid ${(accent ?? VAL_COLOR)}33`,
        color: accent ?? VAL_COLOR, padding: '2px 5px', borderRadius: 3,
        fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
      }}
    />
  )
}

// ─── Pin row helper ────────────────────────────────────────────────────────────
function PinRow({ id, label, pinColor, right = false, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '5px 12px', position: 'relative',
      borderTop: '1px solid #0a1412',
      justifyContent: right ? 'flex-end' : 'flex-start',
    }}>
      {!right && (
        <Handle type="target" position={Position.Left} id={id}
          style={{ background: pinColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${pinColor}55` }} />
      )}
      <span style={{ color: `${pinColor}88`, fontSize: 10 }}>{label}</span>
      {children}
      {right && (
        <Handle type="source" position={Position.Right} id={id}
          style={{ background: pinColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${pinColor}55` }} />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════════
// ARRAY NODES
// ════════════════════════════════════════════════════════════════════════════════

// ── Array Get ─────────────────────────────────────────────────────────────────
export function ArrayGetNode({ data, selected }) {
  const stop = e => e.stopPropagation()
  return (
    <div style={{
      background: selected ? ARR_BG_SEL : ARR_BG_IDLE,
      border: `2px solid ${selected ? ARR_BORDER_SEL : ARR_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: selected ? `0 0 12px ${ARR_ACCENT}22` : 'none',
    }}>
      <div style={{ background: ARR_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '4px 4px 0 0' }}>
        <span style={{ color: ARR_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Array Get</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: `${VAL_COLOR}88`, fontSize: 10 }}>Value</span>
          <Handle type="source" position={Position.Right} id="out-value"
            style={{ background: VAL_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${VAL_COLOR}55` }} />
        </div>
      </div>
      {/* Var row */}
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${ARR_ACCENT}66`, fontSize: 9 }}>ARR</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="array" accent={ARR_ACCENT} />
      </div>
      {/* Index pin */}
      <div style={{ padding: '4px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-index"
          style={{ background: IDX_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${IDX_COLOR}55` }} />
        <span style={{ color: `${IDX_COLOR}88`, fontSize: 10 }}>Index</span>
        <NumberInput value={data.index} onChange={v => data.onChange?.('index', v)} />
      </div>
    </div>
  )
}

// ── Array Set ─────────────────────────────────────────────────────────────────
export function ArraySetNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? ARR_BG_SEL : ARR_BG_IDLE,
      border: `2px solid ${fired ? '#ffcc44' : selected ? ARR_BORDER_SEL : ARR_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${ARR_ACCENT}22` : 'none',
    }}>
      <div style={{ background: ARR_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC} />
        <span style={{ color: ARR_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Array Set</span>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC} />
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${ARR_ACCENT}66`, fontSize: 9 }}>ARR</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="array" accent={ARR_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 3px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-index"
          style={{ background: IDX_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${IDX_COLOR}55` }} />
        <span style={{ color: `${IDX_COLOR}88`, fontSize: 10 }}>Index</span>
        <NumberInput value={data.index} onChange={v => data.onChange?.('index', v)} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-value"
          style={{ background: VAL_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${VAL_COLOR}55` }} />
        <span style={{ color: `${VAL_COLOR}88`, fontSize: 10 }}>Value</span>
        <TextInput value={data.value} onChange={v => data.onChange?.('value', v)} placeholder="value" />
      </div>
    </div>
  )
}

// ── Array Add ─────────────────────────────────────────────────────────────────
export function ArrayAddNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? ARR_BG_SEL : ARR_BG_IDLE,
      border: `2px solid ${fired ? '#ffcc44' : selected ? ARR_BORDER_SEL : ARR_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${ARR_ACCENT}22` : 'none',
    }}>
      <div style={{ background: ARR_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC} />
        <span style={{ color: ARR_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Array Add</span>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC} />
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${ARR_ACCENT}66`, fontSize: 9 }}>ARR</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="array" accent={ARR_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-value"
          style={{ background: VAL_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${VAL_COLOR}55` }} />
        <span style={{ color: `${VAL_COLOR}88`, fontSize: 10 }}>Value</span>
        <TextInput value={data.value} onChange={v => data.onChange?.('value', v)} placeholder="value" />
      </div>
    </div>
  )
}

// ── Array Remove ──────────────────────────────────────────────────────────────
export function ArrayRemoveNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? ARR_BG_SEL : ARR_BG_IDLE,
      border: `2px solid ${fired ? '#ffcc44' : selected ? ARR_BORDER_SEL : ARR_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${ARR_ACCENT}22` : 'none',
    }}>
      <div style={{ background: ARR_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC} />
        <span style={{ color: ARR_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Array Remove</span>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC} />
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${ARR_ACCENT}66`, fontSize: 9 }}>ARR</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="array" accent={ARR_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-index"
          style={{ background: IDX_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${IDX_COLOR}55` }} />
        <span style={{ color: `${IDX_COLOR}88`, fontSize: 10 }}>Index</span>
        <NumberInput value={data.index} onChange={v => data.onChange?.('index', v)} />
      </div>
    </div>
  )
}

// ── Array Length ──────────────────────────────────────────────────────────────
export function ArrayLengthNode({ data, selected }) {
  return (
    <div style={{
      background: selected ? ARR_BG_SEL : ARR_BG_IDLE,
      border: `2px solid ${selected ? ARR_BORDER_SEL : ARR_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: selected ? `0 0 12px ${ARR_ACCENT}22` : 'none',
    }}>
      <div style={{ background: ARR_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '4px 4px 0 0' }}>
        <span style={{ color: ARR_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Array Length</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: `${IDX_COLOR}88`, fontSize: 10 }}>Length</span>
          <Handle type="source" position={Position.Right} id="out-value"
            style={{ background: IDX_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${IDX_COLOR}55` }} />
        </div>
      </div>
      <div style={{ padding: '6px 10px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${ARR_ACCENT}66`, fontSize: 9 }}>ARR</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="array" accent={ARR_ACCENT} />
      </div>
    </div>
  )
}

// ── Array Clear ───────────────────────────────────────────────────────────────
export function ArrayClearNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? ARR_BG_SEL : ARR_BG_IDLE,
      border: `2px solid ${fired ? '#ffcc44' : selected ? ARR_BORDER_SEL : ARR_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${ARR_ACCENT}22` : 'none',
    }}>
      <div style={{ background: ARR_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC} />
        <span style={{ color: ARR_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Array Clear</span>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC} />
      </div>
      <div style={{ padding: '6px 10px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${ARR_ACCENT}66`, fontSize: 9 }}>ARR</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="array" accent={ARR_ACCENT} />
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════════
// MAP NODES
// ════════════════════════════════════════════════════════════════════════════════

// ── Key input — int or string depending on keyType ─────────────────────────────
function KeyInput({ keyType, value, onChange }) {
  if (keyType === 'int') {
    return <NumberInput value={value} onChange={onChange} placeholder="key" />
  }
  return <TextInput value={value} onChange={onChange} placeholder="key" accent={MAP_ACCENT} />
}

// ── Map Get ───────────────────────────────────────────────────────────────────
export function MapGetNode({ data, selected }) {
  const keyType = data.keyType ?? 'string'
  const keyColor = keyType === 'int' ? IDX_COLOR : MAP_ACCENT
  return (
    <div style={{
      background: selected ? MAP_BG_SEL : MAP_BG_IDLE,
      border: `2px solid ${selected ? MAP_BORDER_SEL : MAP_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: selected ? `0 0 12px ${MAP_ACCENT}22` : 'none',
    }}>
      <div style={{ background: MAP_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '4px 4px 0 0' }}>
        <span style={{ color: MAP_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Map Get</span>
        {/* keyType toggle */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 3, overflow: 'hidden', border: `1px solid ${MAP_BORDER_IDLE}` }}>
          {['int', 'string'].map(kt => (
            <button key={kt}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => data.onChange?.('keyType', kt)}
              style={{
                background: keyType === kt ? MAP_HDR_BG : 'transparent',
                border: 'none', color: keyType === kt ? MAP_ACCENT : `${MAP_ACCENT}44`,
                padding: '2px 5px', fontSize: 8, fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}
            >{kt}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: `${VAL_COLOR}88`, fontSize: 10 }}>Value</span>
          <Handle type="source" position={Position.Right} id="out-value"
            style={{ background: VAL_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${VAL_COLOR}55` }} />
        </div>
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${MAP_ACCENT}66`, fontSize: 9 }}>MAP</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="map" accent={MAP_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-key"
          style={{ background: keyColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${keyColor}55` }} />
        <span style={{ color: `${keyColor}88`, fontSize: 10 }}>Key</span>
        <KeyInput keyType={keyType} value={data.key} onChange={v => data.onChange?.('key', v)} />
      </div>
    </div>
  )
}

// ── Map Set ───────────────────────────────────────────────────────────────────
export function MapSetNode({ data, selected }) {
  const fired = isFired(data)
  const keyType = data.keyType ?? 'string'
  const keyColor = keyType === 'int' ? IDX_COLOR : MAP_ACCENT
  return (
    <div style={{
      background: selected ? MAP_BG_SEL : MAP_BG_IDLE,
      border: `2px solid ${fired ? '#ffcc44' : selected ? MAP_BORDER_SEL : MAP_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${MAP_ACCENT}22` : 'none',
    }}>
      <div style={{ background: MAP_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC} />
        <span style={{ color: MAP_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Map Set</span>
        {/* keyType toggle */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 3, overflow: 'hidden', border: `1px solid ${MAP_BORDER_IDLE}` }}>
          {['int', 'string'].map(kt => (
            <button key={kt}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => data.onChange?.('keyType', kt)}
              style={{
                background: keyType === kt ? MAP_HDR_BG : 'transparent',
                border: 'none', color: keyType === kt ? MAP_ACCENT : `${MAP_ACCENT}44`,
                padding: '2px 5px', fontSize: 8, fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}
            >{kt}</button>
          ))}
        </div>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC} />
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${MAP_ACCENT}66`, fontSize: 9 }}>MAP</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="map" accent={MAP_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 3px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-key"
          style={{ background: keyColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${keyColor}55` }} />
        <span style={{ color: `${keyColor}88`, fontSize: 10 }}>Key</span>
        <KeyInput keyType={keyType} value={data.key} onChange={v => data.onChange?.('key', v)} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-value"
          style={{ background: VAL_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${VAL_COLOR}55` }} />
        <span style={{ color: `${VAL_COLOR}88`, fontSize: 10 }}>Value</span>
        <TextInput value={data.value} onChange={v => data.onChange?.('value', v)} placeholder="value" />
      </div>
    </div>
  )
}

// ── Map Remove ────────────────────────────────────────────────────────────────
export function MapRemoveNode({ data, selected }) {
  const fired = isFired(data)
  const keyType = data.keyType ?? 'string'
  const keyColor = keyType === 'int' ? IDX_COLOR : MAP_ACCENT
  return (
    <div style={{
      background: selected ? MAP_BG_SEL : MAP_BG_IDLE,
      border: `2px solid ${fired ? '#ffcc44' : selected ? MAP_BORDER_SEL : MAP_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${MAP_ACCENT}22` : 'none',
    }}>
      <div style={{ background: MAP_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC} />
        <span style={{ color: MAP_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Map Remove</span>
        {/* keyType toggle */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 3, overflow: 'hidden', border: `1px solid ${MAP_BORDER_IDLE}` }}>
          {['int', 'string'].map(kt => (
            <button key={kt}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => data.onChange?.('keyType', kt)}
              style={{
                background: keyType === kt ? MAP_HDR_BG : 'transparent',
                border: 'none', color: keyType === kt ? MAP_ACCENT : `${MAP_ACCENT}44`,
                padding: '2px 5px', fontSize: 8, fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}
            >{kt}</button>
          ))}
        </div>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC} />
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${MAP_ACCENT}66`, fontSize: 9 }}>MAP</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="map" accent={MAP_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-key"
          style={{ background: keyColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${keyColor}55` }} />
        <span style={{ color: `${keyColor}88`, fontSize: 10 }}>Key</span>
        <KeyInput keyType={keyType} value={data.key} onChange={v => data.onChange?.('key', v)} />
      </div>
    </div>
  )
}

// ── Map Has Key ───────────────────────────────────────────────────────────────
export function MapHasKeyNode({ data, selected }) {
  const keyType = data.keyType ?? 'string'
  const keyColor = keyType === 'int' ? IDX_COLOR : MAP_ACCENT
  return (
    <div style={{
      background: selected ? MAP_BG_SEL : MAP_BG_IDLE,
      border: `2px solid ${selected ? MAP_BORDER_SEL : MAP_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: selected ? `0 0 12px ${MAP_ACCENT}22` : 'none',
    }}>
      <div style={{ background: MAP_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '4px 4px 0 0' }}>
        <span style={{ color: MAP_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Map Has Key</span>
        {/* keyType toggle */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 3, overflow: 'hidden', border: `1px solid ${MAP_BORDER_IDLE}` }}>
          {['int', 'string'].map(kt => (
            <button key={kt}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => data.onChange?.('keyType', kt)}
              style={{
                background: keyType === kt ? MAP_HDR_BG : 'transparent',
                border: 'none', color: keyType === kt ? MAP_ACCENT : `${MAP_ACCENT}44`,
                padding: '2px 5px', fontSize: 8, fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}
            >{kt}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: `${BOOL_COLOR}88`, fontSize: 10 }}>Bool</span>
          <Handle type="source" position={Position.Right} id="out-bool"
            style={{ background: BOOL_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${BOOL_COLOR}55` }} />
        </div>
      </div>
      <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${MAP_ACCENT}66`, fontSize: 9 }}>MAP</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="map" accent={MAP_ACCENT} />
      </div>
      <div style={{ padding: '3px 10px 6px', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-key"
          style={{ background: keyColor, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${keyColor}55` }} />
        <span style={{ color: `${keyColor}88`, fontSize: 10 }}>Key</span>
        <KeyInput keyType={keyType} value={data.key} onChange={v => data.onChange?.('key', v)} />
      </div>
    </div>
  )
}

// ── Map Length ────────────────────────────────────────────────────────────────
export function MapLengthNode({ data, selected }) {
  return (
    <div style={{
      background: selected ? MAP_BG_SEL : MAP_BG_IDLE,
      border: `2px solid ${selected ? MAP_BORDER_SEL : MAP_BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: selected ? `0 0 12px ${MAP_ACCENT}22` : 'none',
    }}>
      <div style={{ background: MAP_HDR_BG, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '4px 4px 0 0' }}>
        <span style={{ color: MAP_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Map Length</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: `${IDX_COLOR}88`, fontSize: 10 }}>Length</span>
          <Handle type="source" position={Position.Right} id="out-value"
            style={{ background: IDX_COLOR, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${IDX_COLOR}55` }} />
        </div>
      </div>
      <div style={{ padding: '6px 10px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: `${MAP_ACCENT}66`, fontSize: 9 }}>MAP</span>
        <VarSelect value={data.varName} onChange={v => data.onChange?.('varName', v)} variables={data.variables} filterType="map" accent={MAP_ACCENT} />
      </div>
    </div>
  )
}
